/* Dynamic Plot code copyright 2015-17 (c) Tuva Labs Inc. */
/**
 * Annotation plot elements: freehandLineElement, etc.
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by jpatel and cmiller
 */

import ContextMenuUtil, {CONTEXT_MENU_WIDTH} from '../../../utils/ContextMenuUtil';
import { PlotElement} from './plotElement'

//============================

//Dyn.annotateBaseElement = function() {
//    "use strict";
//    this.lines = []; // array of type Dyn.FreehandLineModel()
//    this.viewPoints = null; 	 // temporary, do not save
//    this.selectedObjectIndex = -1; // temporary, do not save
//};
//Dyn.annotateBaseElement.inheritsFrom( Dyn.PlotElement );

//============================
export const wrapDynAnnotateElement = function(Dyn) {
	let mediator = Dyn.mediator;

Dyn.EAnnotationType = { // values are saved with plots; do not change
    kPencilAT: "Line", // connected line segments
    kRectAT: "Rectangle",
    kCircleAT: "Circle",
    kArrowAT: "Arrow",
    kStraightLineAT: "SLine", // single straight line
    kTextAT: "Text",
    kImageAT: "Image"
};

//const textCornerHandles = true; // debugging: use new style text handles in corners, instead of centered

/**
 * Constructor for a single Freehand Line, created by the Sketch button, and controlled by AnnotateLayerElement.
 * @constructor
 */
class AnnotationObject {

    static controlTypeToAnnotationType( controlType /* Dyn.EPlotControl */ ) {
        switch( controlType ) {
            case Dyn.EPlotControl.kControlsAnnotatePencil:
                return Dyn.EAnnotationType.kPencilAT;
            case Dyn.EPlotControl.kControlsAnnotateRect:
                return Dyn.EAnnotationType.kRectAT;
            case Dyn.EPlotControl.kControlsAnnotateCircle:
                return Dyn.EAnnotationType.kCircleAT;
            case Dyn.EPlotControl.kControlsAnnotateArrow:
                return Dyn.EAnnotationType.kArrowAT;
            case Dyn.EPlotControl.kControlsAnnotateStraightLine:
                return Dyn.EAnnotationType.kStraightLineAT;
            case Dyn.EPlotControl.kControlsAnnotateText:
                return Dyn.EAnnotationType.kTextAT;
            case Dyn.EPlotControl.kControlsAnnotateImage:
                return Dyn.EAnnotationType.kImageAT;
        }
    }

    /**
     * @param optionalType -- one of Dyn.EAnnotationType values
     */
    constructor( optionalType ) {
        this.type = optionalType || Dyn.EAnnotationType.kPencilAT; // set type first!
        this.normalizedPoints = []; // [ NACoord ], x, y, x, y path or bbox coordinates.
        this.color = Dyn.plotUtil.annotationColors[0];
        this.width = this.getDefaultWidth(); // path/line/outline width
        this.isLocked = false; // true, false, or "drag", see isDragLocked()
        this.layerNum = 0; // we default to one layer, layer 0, but can add more (integer) values.

        if( this.isText()) {
            this.bgColor = Dyn.plotUtil.textAnnotationBackgroundColor; // used only for text objects
            this.text = this.isText() ? Dyn.loc("Text") : ""; // used only for text objects
            this.fontSize = Dyn.plotUtil.textAnnotationSizeDefault; // text object font size
            this.textWrapChange = "new"; // do not save, temporary only
        }
        if( this.isImage()) {
            this.imageSrc = ""; // image object: image source URL or data string;
        }
        if( this.isPencil()) {
            this.smoothing = 0; // 0=no smoothing, 1+ is the smoothing factor
        }
        if( this.canFill()) {
            this.isFilled = this.isText(); // default: text is filled, circle/rect/pencil is outline (not filled)
        }
        this.viewBBox = null; // do not save, temporary only
    }

    static initialTextBoxWidth = 150;
    static minTextBoxWidth = 20;
    static maxTextBoxWidth = 2000;
    static layerOptions = [-2, -1, 0, 1, 2];

    getMinWidth() {
        return (this.isText() || this.isImage()) ? 0 : 1; // allow image and text outlines to disappear
    }

    getDefaultWidth() {
        return (this.isText() || this.isImage()) ? 1 : Dyn.plotUtil.annotationLineWidthDefault; // default image and text outlines is thinner
    }

    save() {
        var data = {
            color: this.color,
            type: this.type,
            label: "", // for admins to edit and customize the aria-label, see getCustomAriaLabel()
        };
        if( Number.isFinite( this.width )) {
            // prior to v3.1.0, data.width = this.width, but ignored for text elements
            data.width2 = this.width; // line or outline width, may be 0
        }
        if( this.isLocked ) { // don't save if unlocked, to save space
            data.isLocked = this.isLocked;
        }
        if( this.layerNum ) { // don't save layerNum===0
            data.layerNum = this.layerNum;
        }

        // saved normalized coordinates (for all versions, including pre-2.12.1)
        function toScreenPrecision( n ) {  // save space for long annotations (e.g. pencil)
            return Dyn.mathUtil.roundToPrecision( n, 6 );
        }
        data.normalizedPoints = this.normalizedPoints.map( naCoord => toScreenPrecision( naCoord.n ) );
        // for 2.12.1+, also save the attribute coordinates, if any, to restore in scatterplots correctly (bug fix #1338)
        if( (this.normalizedPoints.length >= 2)
            && ( this.normalizedPoints[0].hasAttributeCoord() || // if numeric attribute on X axis
                this.normalizedPoints[1].hasAttributeCoord())    // and/or numeric attribute on Y axis
        ) {
            data.attribCoords = this.normalizedPoints.map( naCoord => (
                Number.isFinite(naCoord.a) ? toScreenPrecision( naCoord.a ) : null ));
        }

        // type-based parameters LAST
        if( this.isText() ) {
            data.fontSize = this.fontSize;
            data.bgColor = this.bgColor;
            data.text = this.text;
        } else if( this.isImage()) {
            data.imageSrc = this.imageSrc;
        } else if( this.isPencil()) {
            if( Number.isFinite( this.smoothing ))
                data.smoothing = this.smoothing;
        }
        if( this.canFill() ) {
            data.isFilled = this.isFilled;
        }

        return data;
    }

    restore( data ) {
        if( data.normalizedPoints ){
            // restore the array of NACoords from the .n coordinate only (all plots)
            this.normalizedPoints = data.normalizedPoints.map( nCoord => new Dyn.axisUtil.NACoord( nCoord ) );
        }
        if( data.attribCoords ){
            // also restore attribute values for better positioning with numeric attribute on X and/or Y (bug fix #1338)
            Dyn.assert( data.normalizedPoints.length === data.attribCoords.length );
            this.normalizedPoints.forEach( ( naCoord, index ) => {
                if( index < data.attribCoords.length && Number.isFinite( data.attribCoords[index]) ) {
                    naCoord.a = data.attribCoords[index]; // restore attribute coord for odd (X) and/or even (Y) coords.
                }
            } );
        }
        if( data.color ){
            this.color = data.color;
        }
        // restore type; old models with no type are lines (pencil tool)
        this.type = data.type || Dyn.EAnnotationType.kPencilAT;

        // use new default width on old objects
        this.width = (Number.isFinite( data.width ) && ! this.isText()) ? data.width : // v1 width for non-text objects
            Number.isFinite( data.width2 ) ? data.width2 // v2 width for all objects including text
                : this.getDefaultWidth(); // else use default width
        this.isLocked = data.isLocked || false;
        this.layerNum = data.layerNum || 0;
        this.label = data.label; // OK if undefined, see getCustomAriaLabel()

        // type-based restorations LAST
        if( this.isText()) {
            this.text = data.text || ""; // no text? use empty string
            this.fontSize = data.fontSize || Dyn.plotUtil.textAnnotationSizeDefault;
            this.bgColor = data.bgColor || Dyn.plotUtil.textAnnotationBackgroundColor;
        } else if ( this.isImage()) {
            this.imageSrc = (typeof data.imageSrc === 'string') ? data.imageSrc : "";
        } else if( this.isPencil()) {
            this.setPencilSmoothing( data.smoothing );
        }
        // check last, because canFill() may depend on this.isClosedPath
        if( this.canFill() && typeof data.isFilled === 'boolean' ) {
            this.isFilled = data.isFilled;
        }
    }

    isNonEmpty() {
        return this.normalizedPoints.length > 0;
    }

    isSinglePoint() {
        return this.normalizedPoints.length === 2;
    }

    getNumXYPoints() {
        return this.normalizedPoints.length / 2; // x, y, x, y makes 2 (x,y) points
    }

    isPencil() {
        return (this.type === Dyn.EAnnotationType.kPencilAT);
    }

    isRectangle() {
        return (this.type === Dyn.EAnnotationType.kRectAT);
    };

    isCircle() {
        return (this.type === Dyn.EAnnotationType.kCircleAT);
    };

    isArrow() {
        return (this.type === Dyn.EAnnotationType.kArrowAT);
    };

    isStraightLine() {
        return (this.type === Dyn.EAnnotationType.kStraightLineAT);
    }

    isText() {
        return (this.type === Dyn.EAnnotationType.kTextAT);
    }

    isImage() {
        return (this.type === Dyn.EAnnotationType.kImageAT);
    }

    wasClosed() {
        // was it's 'X' close button press, and it therefore should be deleted?
        return this.closed;
    }

    addNormalizedPoint( n ) {
        let p = new Dyn.axisUtil.NACoord( n );
        this.normalizedPoints.push( p );
        return p; // for chaining
    }

    stripAttributeCoord( stripOnX, stripOnY ) {
        "use strict"
        this.normalizedPoints.forEach( ( naCoord, index ) => {
            if( Dyn.mathUtil.isEven( index ) ){
                if( stripOnX ){
                    naCoord.stripAttributeCoord();
                }
            } else {
                if( stripOnY ){
                    naCoord.stripAttributeCoord();
                }
            }
        } );
    }

    getDisplayFontSize() {
        // before being displayed, our stored font size is adjusted by the font size multiplier,
        // so that text annotations shrink and expand with font size preference changes,
        // just like other plot elements.
        return Dyn.preferences.getFontSize( this.fontSize );
    }

    getViewCoordsAndBBox( xAxisView, yAxisView ) {
        // convert the x (even coord) or y (odd coord) from normalized [0-1] to view coordinates [in Raphael paper]
        // also save certain coordinates for positioning handles (closebox, etc)
        // sets: lastNACoord, x, y, xRight, yRight, xLeft, yLeft, x1, y1
        let lastNACoord, x, y, // temps
            points = [], // collected positions of annotation in view coordinates
            bbox = new Dyn.mathUtil.Rect(), // bounding box of points
            pRight;
        this.normalizedPoints.forEach(( naCoord, index ) => {
            if( Dyn.mathUtil.isEven( index ) ) {
                // 0th, 2nd, 4th, ... index is X-coord
                lastNACoord = naCoord;
            } else {
                // 1st, 3rd, 5th, ... index is Y-coord
                x = lastNACoord.getViewCoord( xAxisView );
                y = naCoord.getViewCoord( yAxisView );
                Dyn.assertFiniteNumeric( x );
                Dyn.assertFiniteNumeric( y );
                points.push({ x:x, y:y });
                if( Dyn.mathUtil.isInRange( lastNACoord.n, 0, 1 ) && Dyn.mathUtil.isInRange( naCoord.n, 0, 1 ) ) {
                    if( index===1 || !pRight) {
                        bbox.setFromTwoPoints( {x,y}, {x,y} ); // initialize to first point
                        pRight = {x:x,y:y};
                    } else {
                        bbox.addPoint( x, y );
                        if( x > pRight.x ) {
                            pRight = {x:x,y:y}; // rightmost point within plot interior
                        }
                    }
                }
            }
        });
        if( ! pRight ) {
            pRight = {x:x,y:y}; // use last point if not set from plot interior
        }
        let viewCoords = {
            points: points,
            bbox: bbox,
            pRight: pRight,
            p1: Dyn.dataUtil.clone(points[0]), // first point drawn
            p2: Dyn.dataUtil.clone(points[points.length-1]) // last point drawn (2nd point in rect)
        };
        return viewCoords;
    }

    /** create the Raphael/SVG visible element for this model, add to paper */
    createElementFromModel( paper ) {
        let elem;
        if( this.isText() ) {
            // Text annotations: create a visible bounding rectangle for text, that is also the keyboard focus
            paper.setStart();
            let rectEl = paper.rect( 0, 0, 0, 0 )
                    .attr( {
                            fill: this.isFilled ? this.bgColor : "none",
                            stroke: this.color
                        }),
                textEl = paper.text( 0, 0, this.text )
                    .attr( {
                        fill: this.color,
                        "stroke-width": 1,
                        'text-anchor': 'middle',
                        "font-size": this.getDisplayFontSize()
                    })
                    .hide(),
                textSet = elem = paper.setFinish();
            textSet.getRectElement = (() => rectEl);
            textSet.getTextElement = (() => textEl);
            [ textSet, rectEl, textEl ].forEach( el => {
                el.getFocusElement = (() => rectEl);
                el.getSet = (() => textSet);
            });
            //Dyn.plotUtil.disableMouseOverEvents( textEl ); // #2323: allow focus on text, especially when rect has no fill.
        } else if( this.isImage()) {
            // Image annotations: create a visible bounding rectangle for image, that is also the keyboard focus
            paper.setStart();
            let hasImage = this.imageSrc.length > 0,
                imageEl = paper.image( this.imageSrc, 0, 0, 0, 0 ),
                rectEl = paper.rect( 0, 0, 0, 0 ) // create outline rect
                    .attr( { stroke: this.color }),
                textEl = paper.text( 0, 0, Dyn.loc("Paste image here")) // prompt text on top when visible
                    .attr({
                        fill: this.color,
                        'text-anchor': 'middle',
                        "font-size": this.getDisplayFontSize() })
                    .hide(),
                imageSet = elem = paper.setFinish();
            imageSet.getImageElement = function() { return elem[0]; };
            imageSet.getRectElement = function() { return elem[1]; };
            imageSet.getTextElement = function() { return elem[2]; };
            [ imageSet, imageEl, rectEl, textEl ].forEach( (el) => {
                el.getFocusElement = (() => rectEl);
                el.getSet = (() => imageSet);
            });
            Dyn.plotUtil.disableMouseOverEvents( textEl ); // rect is focus element, not image
        } else {
            // rectangle, circle, etc., are all paths, not a set, so use the path as the focus
            elem = paper.path( "M0,0" );
            elem.attr( {
                    fill:   this.isFilled ? this.color : "none",
                    stroke: this.isFilled ? "none" : this.color,
                    "stroke-width": this.width,
                    "stroke-linecap": "round" });
            elem.getFocusElement = (() => elem);
        }
        // make sure we can navigate between parts of an annotation, if it is a set of elements
        Dyn.assert( elem.getFocusElement());
        Dyn.assert( (elem.type!=='set') || elem.getFocusElement().getSet() );
        return elem;
    }

    /** position the Raphael element(s) for this model */
    updateElementFromModel( annoElement, xAxisView, yAxisView ) {
        // get the collected points from the model, in view coordinates
        let viewCoords = this.getViewCoordsAndBBox( xAxisView, yAxisView );
        Dyn.assert( viewCoords.points.length >= 1, "at least point needed to update");
        if( this.isText()) {
            let bbox = (new Dyn.mathUtil.Rect()).setFromTwoPoints( viewCoords.p1, viewCoords.p2 );
            this._updateTextElementFromModel( annoElement, bbox, xAxisView, yAxisView );
        } else if( this.isImage()) {
            let bbox = (new Dyn.mathUtil.Rect()).setFromTwoPoints( viewCoords.p1, viewCoords.p2 );
            this._updateImageElementFromModel( annoElement, bbox, xAxisView, yAxisView );
        } else {
            this._updatePathBasedElementFromModel( annoElement, viewCoords, xAxisView, yAxisView);
        }
        this.setViewBBox( annoElement, viewCoords );
    }

    _updatePathBasedElementFromModel( annoElement, viewCoords, xAxisView, yAxisView ) {
        const points = viewCoords.points,
            p1 = viewCoords.p1,
            p2 = viewCoords.p2,
            x1=p1.x,
            y1=p1.y,
            x2=p2.x,
            y2=p2.y;
        let pathString="";

        if( this.isSinglePoint() ) {
            // non-text object does not yet have two separate points, so make a visible path from the single point.
            pathString = "M" + (x1 - 0.1).toFixed( 1 ) + "," + (y1 - 0.1).toFixed( 1 ) + "L" + (x1 + 0.1).toFixed( 1 ) + "," + (y1 + 0.1).toFixed( 1 );
        } else if( this.isPencil()) {
            // create/update freehand line or single point path; see setPencilSmoothing()+updateControlPointsSet()
            this.controlPoints = []; // not saved, for rendering use only
            if( (this.smoothing > 0) && (points.length >= 3)) {
                // convert the smoothing factor to the square of the distance between control points, if factor 1+
                // e.g 0 => 0, 1 => 5^3, 2 = > 6^3, etc.
                let minDistanceSquared = (this.smoothing >= 1) ? Math.pow( Dyn.plotUtil.pencilLineMinPixels + this.smoothing, 3 ) : 0;
                pathString = Dyn.plotUtil.makeSmoothPolyLinePath( points, minDistanceSquared, this.controlPoints );
            }
            if( pathString === "") { // if no smoothing, make a polyline
                pathString = Dyn.plotUtil.makePolyLinePath( points );
                this.controlPoints = points; // show polyline points
            }
        } else if( this.isCircle() ) {
            // update circle path
            pathString = Dyn.plotUtil.makeCirclePath( x1, y1, Dyn.mathUtil.distance( {x:x1,y:y1}, {x:x2,y:y2}));
        } else if( this.isArrow() ) {
            // update arrow path
            pathString = Dyn.plotUtil.makeArrowPath( x1, y1, x2,  y2, Dyn.plotUtil.arrowAnnotationLength, Dyn.plotUtil.arrowAnnotationWidth );
        } else if ( this.isStraightLine() ) {
            // update straight line path
            pathString = Dyn.plotUtil.makeStraightLinePath( x1, y1, x2,  y2 );
        } else {
            // update rectangle path
            Dyn.assert( this.isRectangle(), "unknown annotation type" );
            if( this.isSinglePoint() ) {
                // instead of line draw a single point as a path
                pathString = "M" + (x1 - 0.1).toFixed( 1 ) + "," + (y1 - 0.1).toFixed( 1 ) + "L" + (x1 + 0.1).toFixed( 1 ) + "," + (y1 + 0.1).toFixed( 1 );
            } else {
                let rect = (new Dyn.mathUtil.Rect()).setFromTwoPoints( p1, p2 );
                pathString = Dyn.plotUtil.makeRectanglePath( rect );
            }
        }
        annoElement.attr( {
            path: pathString,
            "stroke-width": this.width,
            fill:   this.isFilled ? this.color : "none",
            stroke: this.isFilled ? "none" : this.color, } );
    }

    /** Use the annotation model to update the visible text annotation. The bounding rectangle and/or
     * the text wrap may need to be adjusted. */
    _updateTextElementFromModel( annoElement, bboxRect, xAxisView, yAxisView ) {
        let annoModel = this,
            textEl = annoElement.getTextElement(),
            rectEl = annoElement.getRectElement();
        updateTextElement( textEl, rectEl, annoModel, bboxRect, this.width ); // first pass of update
        annoElement.show();
        if( annoModel.textWrapChange === "" ) {
            // unchanged text: update the bounding box to fit the existing text wrap (newlines in string)
            updateTextBoxRect( bboxRect, textEl.getBBox( Dyn.plotUtil.textBBoxBeforeTransform ));
        } else {
            // text is new, or has been edited or the boundary resized: therefore RE-WRAP the text
            const minWrapWidth = 10; //Dyn.plotUtil.getTextBBox()
            let wrapWidth = Math.max( minWrapWidth, bboxRect.width - 2*Dyn.plotUtil.textAnnotationBorderDx );
            let textBox = Dyn.plotUtil.wrapStringToWidth( textEl, wrapWidth, true );
            annoModel.text = textEl.attr('text'); // save new wrapping (spaces replaced with newlines)
            updateTextBoxRect( bboxRect, textBox );
            saveTextBoxRect( bboxRect );
        }
        updateTextElement( textEl, rectEl, annoModel, bboxRect, this.width ); // 2nd pass update
        annoModel.viewBBox = bboxRect;
        annoModel.textWrapChange = ""; // change was completed

        function updateTextBoxRect( rectBBox, textBBox ) {
            //if( textCornerHandles ) {
                // if needed, expand rectBBox to at least fit the height of textBBox, from top, with border around text
                // and the same for the width, if the wrapped text has words that are still wider than the rect width
                rectBBox.width = Math.max( rectBBox.width, textBBox.width + 2 * Dyn.plotUtil.textAnnotationBorderDx );
                rectBBox.height = Math.max( rectBBox.height, textBBox.height + 2 * Dyn.plotUtil.textAnnotationBorderDy );
            // } else {
            //     // expand or shrink the rectBBox to match height and width of textBBox, from center, with border around text
            //     let newHeight = textBBox.height + 2 * Dyn.plotUtil.textAnnotationBorderDy,
            //         minWidth = textBBox.width + 2 * Dyn.plotUtil.textAnnotationBorderDx;
            //     rectBBox.expand( Math.max( 0, (minWidth - rectBBox.width) / 2 ), (newHeight - rectBBox.height) / 2 );
            // }
        }

        function saveTextBoxRect( bbox ) {
            // save the new text box size with as normalized coordinates for correct restore
            //if( textCornerHandles ) {
                annoModel.normalizedPoints[0].updateFromViewCoord( bbox.left(), xAxisView );
                annoModel.normalizedPoints[1].updateFromViewCoord( bbox.top(), yAxisView );
                annoModel.normalizedPoints[2].updateFromViewCoord( bbox.right(), xAxisView );
                annoModel.normalizedPoints[3].updateFromViewCoord( bbox.bottom(), yAxisView );
            // } else {
            //     let cx = bbox.centerX(),
            //         cy = bbox.centerY();
            //     annoModel.normalizedPoints[0].updateFromViewCoord( cx - bbox.width / 2, xAxisView );
            //     annoModel.normalizedPoints[1].updateFromViewCoord( cy - bbox.height / 2, yAxisView );
            //     annoModel.normalizedPoints[2].updateFromViewCoord( cx + bbox.width / 2, xAxisView );
            //     annoModel.normalizedPoints[3].updateFromViewCoord( cy + bbox.height / 2, yAxisView );
            // }
        }

        function updateTextElement( textEl, rectEl, annoModel, bbox, width ) {
            rectEl.attr( { x:bbox.left(), y:bbox.top(), width:bbox.width, height:bbox.height,
                "stroke-width": width, stroke: annoModel.color, fill: (annoModel.isFilled ? annoModel.bgColor : "none") });
            textEl.attr( { text: annoModel.text, x:bbox.centerX(), y:bbox.centerY(),
                fill: annoModel.color, "font-size": annoModel.getDisplayFontSize() } );
        }

    }

    /** Use the annotation model to update the visible image annotation. The bounding rectangle
     * may be adjusted to show the "paste image here" prompt. */
    _updateImageElementFromModel( annoElement, bbox, xAxisView, yAxisView ) {
        // update text element to match the model
        let annoModel = this,
            textEl = annoElement.getTextElement(),
            imageEl = annoElement.getImageElement(),
            rectEl = annoElement.getRectElement(),
            hasImage = this.imageSrc.length > 0;
        if( ! hasImage ) { // if no source has been pasted
            // if no source pasted yet, show the text prompt
            // expand or shrink the rectBBox to match height and width of textBBox, from center, with border around text
            let textBBox = textEl.getBBox();
            let minHeight = textBBox.height + 2*Dyn.plotUtil.textAnnotationBorderDy,
                minWidth = textBBox.width + 2*Dyn.plotUtil.textAnnotationBorderDx;
            if( bbox.width < minWidth || bbox.height < minHeight ) {
                bbox.width = Math.max( textBBox.width, bbox.width );
                bbox.height = Math.max( textBBox.height, bbox.height );
                bbox.expand( Math.max( 0, (minWidth - bbox.width) / 2 ), Math.max(0, (minHeight - bbox.height) / 2 ));
                saveExpandedImageRect( bbox );
            }
        }
        // update the SVG elements of the image
        textEl.attr({ x: bbox.centerX(), y: bbox.centerY() });
        rectEl.attr( { x:bbox.x, y:bbox.y, width:bbox.width, height:bbox.height, "stroke-width": this.width });
        imageEl.attr({ x:bbox.x, y:bbox.y, width:bbox.width, height:bbox.height });
        this.updateImagePrompt( annoElement );

        function saveExpandedImageRect( bbox ) {
            // save image size with as normalized coordinates for correct restore
            annoModel.normalizedPoints[0].updateFromViewCoord( bbox.left(), xAxisView );
            annoModel.normalizedPoints[1].updateFromViewCoord( bbox.bottom(), yAxisView );
            annoModel.normalizedPoints[2].updateFromViewCoord( bbox.right(), xAxisView );
            annoModel.normalizedPoints[3].updateFromViewCoord( bbox.top(), yAxisView );
        }
    }

    /** update the annotation model and visible image annotation when we have a new image source */
    updateImageSrc( annoElement, pastedImageSrc ) {
        // save with model
        Dyn.assert( this.isImage());
        this.imageSrc = pastedImageSrc;
        console.log("updated image source, data size=", this.imageSrc.length );

        // update the SVG elements of the image
        let imageEl = annoElement.getImageElement();
        imageEl.attr( { src: this.imageSrc } );
        this.updateImagePrompt( annoElement );
    }

    updateImagePrompt( annoElement ) {
        Dyn.assert( this.isImage());
        let hasImage = this.imageSrc.length > 0,
            rectEl = annoElement.getRectElement(),
            textEl = annoElement.getTextElement();
        rectEl.attr( { fill: hasImage? "none" : this.bgColor });
        Dyn.plotUtil.showOrHideElement( textEl, !hasImage );
    }

    /** get the name localized, for undo/redo, etc., e.g. "Pencil Annotation"  */
    getLocName() {
        const getName = () => {
            switch( this.type ) {
                case Dyn.EAnnotationType.kPencilAT:
                    return Dyn.loc( "Pencil" );
                case Dyn.EAnnotationType.kRectAT:
                    return Dyn.loc( "Rectangle" );
                case Dyn.EAnnotationType.kCircleAT:
                    return Dyn.loc( "Circle" );
                case Dyn.EAnnotationType.kArrowAT:
                    return Dyn.loc( "Arrow" );
                case Dyn.EAnnotationType.kStraightLineAT:
                    return Dyn.loc( "Line" );
                case Dyn.EAnnotationType.kTextAT:
                    return Dyn.loc( "Text" );
                case Dyn.EAnnotationType.kImageAT:
                    return Dyn.loc( "Image" );
                default:
                    Dyn.assert( false, "unknown type:" + this.type );
                    return "";
            }
        }
        return Dyn.loc("{{object}} Annotation", { object: Dyn.loc( getName()) });
    }

    /** allow a custom aria-label */
    getCustomAriaLabel() {
        if( this.label && typeof this.label === 'string' ) {
            return this.label; // non-empty custom label, like "Duck image"
        } else {
            return this.getLocName(); // standard label, like "Image Annotation"
        }
    }

    /**
     * Once the annotation has been positioned, set the bounding box, determine where buttons will
     * go once it is selected.
     * @param annoElement
     * @param viewCoords
     */
    setViewBBox( annoElement, viewCoords ) {
        // save data for positioning closeBox, context menu button, resize handles (see selectAdornment())
        this.viewBBox = new Dyn.mathUtil.Rect( annoElement.getFocusElement().getBBox() );
        this.viewBBox.p1 = viewCoords.p1;
        this.viewBBox.p2 = viewCoords.p2;
        this.viewBBox.pRight = viewCoords.pRight;
        if( viewCoords.points.length === 2 ) {
            // which are the left, right, top, bottom normalizedPoints coordinate indexes in a rectangular object?
            const points = viewCoords.points, pRight = viewCoords.pRight;
            this.viewBBox.coordIndex = {
                right: (points[0].x === pRight.x) ? 0 : 2,
                left: (points[0].x === pRight.x) ? 2 : 0,
                top: (points[0].y < points[1].y) ? 1 : 3,
                bottom: (points[0].y < points[1].y) ? 3 : 1
            };
        }
        // now specify the reference points depending on the type of annotation
        this.viewBBox = Object.assign( this.viewBBox, this.setHandlePositions());
    }

    /**
     * specify the position of the buttons (menu and closebox) and resize handles
     */
    setHandlePositions() {
        const bbox = this.viewBBox;
        switch( this.type ) {
            case Dyn.EAnnotationType.kPencilAT:
                return {
                    numHandles: 0, // no resize handles
                    refRight: [ bbox.pRight.x, bbox.pRight.y ],
                    refLeft: [ bbox.p1.x, bbox.p1.y ],
                }
            case Dyn.EAnnotationType.kRectAT:
            case Dyn.EAnnotationType.kImageAT:
                return {
                    numHandles: 2, // use 2 resize handles
                    refRight: [ bbox.right(), bbox.top() ], // position context menu and closebox buttons to the right of this point
                    refLeft: [ bbox.left(), bbox.top() ], // alternate position of buttons on the left
                    handle1: [ bbox.left(), bbox.bottom(), "sw-resize"], // handle 1: x, y, cursor type
                    handle2: [ bbox.right(), bbox.top(), "ne-resize" ], // handle 1: x, y, cursor type
                }
            case Dyn.EAnnotationType.kCircleAT:
                return {
                    numHandles: 2,
                    refRight: [ bbox.right(), bbox.centerY() ],
                    refLeft: [ bbox.left(), bbox.centerY() ],
                    handle1: [ bbox.right(), bbox.centerY(), "ew-resize"],
                    handle2: [ bbox.left(), bbox.centerY(), "ew-resize" ]
                }
            case Dyn.EAnnotationType.kArrowAT:
            case Dyn.EAnnotationType.kStraightLineAT:
                return {
                    numHandles: 2,
                    refRight: [ bbox.pRight.x, bbox.pRight.y ],
                    refLeft: [ bbox.pRight.x, bbox.pRight.y ],
                    handle1: [ bbox.p1.x, bbox.p1.y, "default"],
                    handle2: [ bbox.p2.x, bbox.p2.y, "default" ]
                }
            case Dyn.EAnnotationType.kTextAT:
                //if( textCornerHandles ) {
                    return {
                        numHandles: 2,
                        refRight: [ bbox.right(), bbox.top() ],
                        refLeft: [ bbox.left(), bbox.top() ],
                        handle1: [ bbox.right(), bbox.top(), "ne-resize" ],
                        handle2: [ bbox.right(), bbox.bottom(), "se-resize" ]
                    }
                // } else {
                //     return {
                //         numHandles: 2,
                //         refRight: [ bbox.right(), bbox.centerY() ],
                //         handle1: [ bbox.left(), bbox.centerY(), "w-resize" ],
                //         handle2: [ bbox.right(), bbox.centerY(), "e-resize" ]
                //     }
                // }
            default:
                Dyn.assert(false,"unknown type:"+this.type); return "";
        }
    }

    /** set or clear the lock to prevent editing of the object.
     * @param type -- boolean or string for partial lock state.
     */
    setLock( type ) {
        switch( type ) {
            case true:
                // all object size, position, and editable parameters are locked
                // object can be selected, in order to lock and unlock.
                this.isLocked = type;
                break;
            case "moveOK":
                // can be moved - drag with mouse or move with arrow keys OK,
                // but other editable params like resize, color are locked
                this.isLocked = type;
                break;
            case "textEditOK":
                // text can be edited, with limited resizing to make sure text is visible;
                // other editable params like move, color are locked.
                Dyn.assert( this.isText());
                this.isLocked = type;
                break;
            default:
                // unlocked if false or undefined
                this.isLocked = false;
        }
        console.log("setLock", this.isLocked, this.type );
    }

    /** is the object locked? Examples:
     * hasLock( true) - is the object in the fully locked state?
     * hasLock( "moveOK" ) - is the object in the locked-but-move-OK state?
     * hasLock( "forMove" ) - is moving the object prevented (e.g. either fully locked or
     * @param type
     * @returns {boolean}
     */
    hasLock( type ) {
        switch( type ) {
            // tests for exact lock state:
            case true:
                return this.isLocked===true; // true if locked for all editing and dragging
            case false:
                return ! this.isLocked; // true if no lock (undefined or false)
            case "moveOK":
                return this.isLocked==="moveOK"; // true if locked, except for moving/repositioning
            case "textEditOK":
                return this.isLocked==="textEditOK"; // true if locked, except for text editing
            // tests for a particular editing operation:
            case "forEdit":
                return !!this.isLocked; // true if we can not edit at all
            case "forMove":
                return (!!this.isLocked) && (this.isLocked !== "moveOK"); // true if we can not move
            case "forTextEdit":
                return (!!this.isLocked) && (this.isLocked !== "textEditOK"); // true if we can not text edit
            // warn of invalid type:
            default:
                Dyn.assert( false, "hasLock unknown type: "+type );
                return true;
        }
    }

    /** can this type of annotation be either an outline or filled shape? If not return false */
    canFill() {
        switch(this.type) {
            case Dyn.EAnnotationType.kPencilAT:
            case Dyn.EAnnotationType.kRectAT:
            case Dyn.EAnnotationType.kCircleAT:
            case Dyn.EAnnotationType.kTextAT:
                return true;
        }
        return false;
    }

    /** turn filling on or off. An unfilled object is drawn in outline only. */
    setFilled( wantFill ) {
        if( this.canFill()) {
            this.isFilled = Boolean(wantFill); // create or update .isFilled
        } else {
            // ok to remove the .isFilled parameter if an object is not fillable,
            // e.g. pencil objects with an open path.
            Dyn.assert( ! wantFill, "not a fill-able annotation type");
            delete this.isFilled;
        }
    }

    /**
     * Return true if the context menu for this object should have a fill control.
     * For the Pencil line, this is variable, because, as drawn after smoothing,
     * it must have enough area to be filled. */
    allowFillOnOffControl() {
        let allowFill = this.canFill(); // for most object types
        if( this.isPencil() && this.isFilled !== true ) {
            // if pencil is filled, always enable the control, to allow user to turn fill off.
            // if not filled, ensure that the fill area is non-zero, otherwise the object will disappear, and not be selectable
            const minDist = Dyn.plotUtil.pencilMinFillDist, minArea = (minDist*minDist);
            // bbox sides are >= minimum distance?
            allowFill  = ( this.viewBBox && this.viewBBox.width >= minDist && this.viewBBox.height >= minDist);
            // sum of triangles every 3 points are >= min area?
            let sumOfTriangleAreas = 0, i=2, pp = this.controlPoints;
            while( allowFill && (i < this.controlPoints?.length )) {
                sumOfTriangleAreas += Dyn.plotUtil.areaOfTriangle( pp.at(i), pp.at(1-2), pp.at(i-2));
                if( sumOfTriangleAreas >= minArea) break;
                ++i;
            }
            allowFill = allowFill && ( sumOfTriangleAreas >= minArea);
            // console.log("allowFillOnOffControl()", "Sqrt bbox="+Math.sqrt(this.viewBBox.width, this.viewBBox.height), "Sqrt trianges="+Math.sqrt(sumOfTriangleAreas),
            //     allowFill ? "ALLOWED": "NOT allowed" );
        }
        return allowFill;
    }

    /** turn on/off or change the line smoothing factor for Pencil objects.
     * The smoothing factor determines the placement of the control points;
     * 0 = no smoothing, 1+ = make control points, increasingly far apart.
     * @param factor {number}
     */
    setPencilSmoothing( factor ) {
        Dyn.assert( this.isPencil());
        this.smoothing = factor || 0; // start smoothing at the given factor, 0 === no smoothing
        Dyn.assert( Number.isFinite(this.smoothing));
    }

    /** show and update the control points for this object, if it has control points. */
    updateControlPointsSet( set ) {
        const controlPointRadius = 2,
            controlPointAttrs = { stroke: "black", fill: "yellow" };
        if( set.length === this.controlPoints?.length ) {
            // reposition control points
            this.controlPoints.forEach( (p, i) => {
                set[i].attr( { cx: p.x, cy: p.y } );
            });
        } else {
            // erase old control points; create new if any
            AnnotationObject.hideControlPointsSet( set );
            this.controlPoints?.forEach( ( p ) => {
                let circle = set.paper.circle( p.x, p.y, controlPointRadius )
                    .attr( controlPointAttrs );
                Dyn.plotUtil.disableMouseOverEvents( circle );
                set.push( circle );
            } );
        }
    }

    /** hide the control points for this object, given the paper.set() that holds them. */
    static hideControlPointsSet( set ) {
        // remove and destroy any previous control points from the set
        while( set.length ) {
            set.pop().remove();
        }
    }
}

Dyn.AnnotationObject = AnnotationObject;

//============================

/**
 * Dyn.AnnotateLayerElement()
 * Controls a set of line overlays in the plot.
 * @param [optionalType] -- EAnnotationType
 * @param [optionalSavedData] -- copied data from AnnotationObject.save()
 * @constructor
 */
    class AnnotateLayerElement extends PlotElement {
        constructor( Dyn, optionalType, optionalSavedObject ) {
            super( Dyn );
            this.initElement( {
                priority: Dyn.EPlotPriority.kPriorityFreehandLine,
                replaceType: Dyn.EPlotControl.kControlsAnnotations,
                primaryType: Dyn.EPlotControl.kControlsAnnotations,
                otherControls: [
                    Dyn.EPlotControl.kControlsAdornmentPositionOnly ] // for fast add/remove since icons/margins don't change
            }, Dyn );
            let newAnnotation = new Dyn.AnnotationObject( optionalType );
            if( optionalSavedObject ) {
                newAnnotation.restore( optionalSavedObject ); // for paste: create object from saved copy
            }
            this.objects = [ newAnnotation ]; // array of type Dyn.AnnotationObject()
            this.updateAnnotationTypes(); // update flags like Dyn.EPlotControl.kControlsAnnotatePencil
            this.dragData = null;           // temporary, do not save
            this.selectedObjectIndex = -1;  // temporary, do not save
            this.xAxisAttributeID = "";     // if non-empty, track object in the attribute's coordinates
            this.yAxisAttributeID = "";     // if non-empty, track object in the attribute's coordinates
        }

        /** override default undo action */
        getUndoAction( contextHints ) {
            // to get "Undo Pencil Annotation for contextHints.action==="addElement"
            // to get "Undo Remove Pencil Annotation for contextHints.action==="removeElement"
            // or get "Undo Move Rectangle Annotation for others
            let annoIndex = Number.isFinite(contextHints?.annoIndex) ?
                    contextHints.annoIndex : (this.selectedObjectIndex>=0) ? // use caller-supplied object index
                    this.selectedObjectIndex : // or the selected object
                    0, // or the 1st object as the default
                annoModel = this.objects[ annoIndex ];
            return annoModel.getLocName();
        }

        // update our flags that show which annotation types are present
        updateAnnotationTypes() {
            /**
             * @param array {[AnnotationObject]}
             * @param booleanFunction {string} -- e.g. "isPencil" for AnnotationObject.isPencil()
             * @return {boolean} -true if we have at least one model of this annotation type, non-closed, in the array
             */
            function arrayHasAnnotationType( array, booleanFunction ) {
                return Boolean( array.find( o => (o[booleanFunction]() && ! o.wasClosed())));
            }
            this[ Dyn.EPlotControl.kControlsAnnotatePencil  ] = arrayHasAnnotationType( this.objects, "isPencil" );
            this[ Dyn.EPlotControl.kControlsAnnotateRect    ] = arrayHasAnnotationType( this.objects, "isRectangle" );
            this[ Dyn.EPlotControl.kControlsAnnotateCircle  ] = arrayHasAnnotationType( this.objects, "isCircle" );
            this[ Dyn.EPlotControl.kControlsAnnotateArrow   ] = arrayHasAnnotationType( this.objects, "isArrow" );
            this[ Dyn.EPlotControl.kControlsAnnotateStraightLine ] = arrayHasAnnotationType( this.objects, "isStraightLine" );
            this[ Dyn.EPlotControl.kControlsAnnotateText    ] = arrayHasAnnotationType( this.objects, "isText" );
            this[ Dyn.EPlotControl.kControlsAnnotateImage    ] = arrayHasAnnotationType( this.objects, "isImage" );
        }

        // save the annotation object models, typically used for save/restore or undo/redo of plot.
        save() {
            // get PlotElement base model
            let plotElementModel = super.save();

            // add the objects to the model
            plotElementModel.objects = [];
            this.objects.forEach( obj =>
                plotElementModel.objects.push( obj.save()));

            return plotElementModel;
        }

        // restore the annotation objects, typically used for save/restore or undo/redo of plot
        restore( savedData ) {
            let Dyn = this.dyn;

            // restore base model
            super.restore( savedData );

            // restore the objects
            this.objects = [];
            if( savedData.objects ) {
                savedData.objects.forEach( value => {
                    if( value ) {
                        let annoModel = new Dyn.AnnotationObject( value.type );
                        annoModel.restore( value );
                        this.objects.push( annoModel );
                    }
                } );
            }
            this.isNewlyRestoredData = true; // to prevent calcAxis() from stripping attribute coordinates
        }

        /**
         * {OVERRIDE}
         * Return true if this (new) plot element should replace the given existing plot element.
         * Override the default behavior to copy annotate objects from existing AnnotateLayerElement.
         * @param iExistingPlotElement
         * @param ioPlotAdornments
         * @returns {boolean}
         */
        replaces( iExistingPlotElement, ioPlotAdornments ) {

            Dyn.assert( this.replaceType !== Dyn.EPlotControl.kControlsNothing, 'invalid control type' );
            var isAnotherAnnotateLayerElement = (this.replaceType === iExistingPlotElement.replaceType && this !== iExistingPlotElement),
                lastLine = null,
                newLines = null;

            if( isAnotherAnnotateLayerElement ) {
                // inherit the last annotation layer element's list of objects (except empty or deleted objects)
                let lastObjectIsNewEmpty = (this.objects.length >= 1) && (!this.objects.at(-1).isNonEmpty()),
                    oldObjects = iExistingPlotElement.objects.filter( ao => (ao.isNonEmpty() && !ao.closed))
                this.objects = oldObjects.concat( this.objects ); // add new (empty) object at end
                iExistingPlotElement.objects = []; // transfer complete so delete from existing AnnotateLayerElement

                // new line color should adopt same color as last line, in case user has changed it and wants all colors to be the same
                // note: do this only for a new empty object; ignore if new object is complete, e.g. pasted copy of another object
                if( this.objects.length >= 2 && lastObjectIsNewEmpty ) {
                    this.objects.at(-1).color = this.objects.at(-2).color;
                }

                // update our status
                this.updateAnnotationTypes();

                // return true to replace the old AnnotateLayerElement -- we will use its objects and adornments
                // because adornments are designed to have only one element controlling them per type
                return true;
            }
            // for other element types call parent function
            return super.replaces( iExistingPlotElement, ioPlotAdornments );
        }

        /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
        calcAxis( ioWhatChanged, subViewRef) {


            // update the X- or Y-axis attribute ID, and clear any old attribute coordinates
            //      This causes the objects to stay in the same relative view position (via normalized coordinates)
            //          even when the axis has completely different attribute coordinates.
            //      Otherwise, the object will stay fixed relative to the attribute coordinate position
            let xData = Dyn.axisUtil.isNewAttributeCoord( this.xAxisAttributeID, subViewRef.xAxisView, this.isNewlyRestoredData ),
                yData = Dyn.axisUtil.isNewAttributeCoord( this.yAxisAttributeID, subViewRef.yAxisView, this.isNewlyRestoredData );
            if( xData.needToStripCoords || yData.needToStripCoords ) {
                this.objects.forEach( function ( item ) {
                    item.stripAttributeCoord( xData.needToStripCoords, yData.needToStripCoords );
                } );
            }
            this.xAxisAttributeID = xData.newAttributeID;
            this.yAxisAttributeID = yData.newAttributeID;
            delete this.isNewlyRestoredData; // no longer newly restored data, if any (issue #1338)
        }

        /**
         * Update adornments (specific to this element) to show a movable reference line on the X-axis of plot area
         */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView /*, noAnimation*/ ) {
            var annotationAdornments = ioAdornArray,
                numAnnoAdornments,
                paper = this.plotShaper.adornments.paper,
                plotElement = this,
                viewOnly = Dyn.preferences.isViewOnly();

            // removed any objects that have been closed by clickedOnCloseBox()
            for( let i = this.objects.length - 1; i >= 0; i-- ) {
                if( this.objects[i].closed ) {
                    this.objects.splice( i, 1 );
                }
            }
            numAnnoAdornments = this.kNumFixedAdornments + (this.objects.length * this.kNumAdornmentsPerLine);

            // create the elements once, if needed
            if( annotationAdornments.length !== numAnnoAdornments ) {
                Dyn.plotUtil.removeAndEmptyArrayOfElements( annotationAdornments );

                this.objects.forEach( ( annoModel, index ) => {
                    let elem = annoModel.createElementFromModel( paper );
                    elem.data( "annoIndex", index )
                        .data( "isText", annoModel.isText());
                    if( annoModel.isText() && !viewOnly ) {
                        paper.inlineTextEditing( elem.getTextElement(), { type: 'textarea' } ); // initialize Raphaël.InlineTextEditing (Note: apparently no "deinitialize" method exists)
                    }
                    if( !viewOnly ) {
                        elem.attr( "cursor", ! annoModel.hasLock("forMove") ? Dyn.plotUtil.cursorMove : Dyn.plotUtil.cursorDefault );
                        elem.drag( dragMove, dragStart, dragEnd ); // works for dragging text, image, path
                        let focusElement = elem.getFocusElement();
                        focusElement.click( clickAnnotation );
                        if( annoModel.isImage()) {
                            // note: add as a 2nd clickable area, because disableMouseOverEvents() does not seem to work for image
                            elem.getImageElement().click( clickAnnotation );
                        }
                        if( annoModel.isText()) {
                            elem.getTextElement().click( clickAnnotation ); // #2323: click on text is needed when no background color
                        }
                        Dyn.plotUtil.setKeyboardFocusable( focusElement, keyDownHandler.bind( focusElement ), annoModel.getCustomAriaLabel() );
                    }
                    if( !Dyn.environment.isTouchDevice() ) {
                        elem.hover( hoverIn, hoverOut );
                    }
                    annotationAdornments.push( elem );
                } );

                // two resize handles to use as needed, 4th and 3rd-to-last elements, see _getResizeHandle()
                annotationAdornments.push( this.makeResizeHandle( annotationAdornments, xAxisView, yAxisView, 1 ).hide() );
                annotationAdornments.push( this.makeResizeHandle( annotationAdornments, xAxisView, yAxisView, 2 ).hide() );
                // control points for pencil objects, see _getControlPointsSet()
                annotationAdornments.push( this.makeControlPointsSet( annotationAdornments ).hide() );
                // context menu button, 2nd-to-last element, see _getContextMenuButton()
                annotationAdornments.push( this.makeContextMenuButton( annotationAdornments, xAxisView, yAxisView ).hide() );
                // "x" style closebox, as last element, see _getCloseBox()
                annotationAdornments.push( this.makeCloseBox().hide() );
            }
            Dyn.assert( annotationAdornments.length === numAnnoAdornments );

            // update them (also positions them for the first time)
            this.objects.forEach( ( annoModel, index ) => {
                plotElement.updateElementFromNormalCoords( annotationAdornments, index, xAxisView, yAxisView );
            } );
            this.updateLayers();
            if( this.selectedObjectIndex >= 0 ) {
                this.selectAdornment( this.selectedObjectIndex ); // update position of controls if plot has moved objects (e.g. axis drag)
            }

            function keyDownHandler( e ) {
                let annoElement = this.getFocusElement(),
                    annoIndex = annoElement.data( 'annoIndex' ),
                    annoModel = plotElement.objects[annoIndex];
                switch( e.key ) { // const {key} = event; ES6+
                    case "Delete":
                    case "Backspace":
                        if( !annoModel.hasLock( "forEdit" ) ) {
                            plotElement.selectAdornment( annoIndex );
                            plotElement._getDeleteHandler()( e );
                        }
                        break;
                    case "Enter":
                        plotElement.selectAdornment( annoIndex );
                        break;
                    case "ArrowLeft":
                    case "ArrowRight":
                    case "ArrowUp":
                    case "ArrowDown": // move this element one pixel in arrow's direction
                        if( !annoModel.hasLock( "forMove" ) ) {
                            let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key );
                            dragStart.call( this, e );
                            dragMove.call( this, dist.dx, dist.dy, undefined, undefined, e );
                            dragEnd.call( this, e );
                        }
                        break;
                    case "u":
                    case "Escape":
                    if( Dyn.plotUtil.isDrawModeAuthoring()) {
                        annoModel.setLock(false);
                        annoElement.attr( "cursor", ! annoModel.hasLock("forMove") ? Dyn.plotUtil.cursorMove : Dyn.plotUtil.cursorDefault );
                        plotElement.selectAdornment( annoIndex ); // update controls
                    }   break;
                }
            }


            /** handle paste of images to image annotation; based on https://jsfiddle.net/bt7BU/225/ */
            // does not work, use AnnotateLayerElement.getPasteHandler() with TuvaDataTool.onWindowPaste() instead.
            // function pasteClipboardHandler(event) {
            //     console.log("pasteClipboardHandler", event, this );
            //     let annoElement = this.getFocusElement(),
            //         annoIndex = annoElement.data( 'annoIndex' ),
            //         annoModel = plotElement.objects[annoIndex];
            //     Dyn.assert( annoModel.isImage());
            //     if( plotElement._isLocked( annoElement )) {
            //         return;
            //     }
            //     // use event.originalEvent.clipboard for newer chrome versions
            //     let items = (event.clipboardData  || event.originalEvent.clipboardData).items;
            //     console.log("pasteClipboardHandler() got these mime types:", JSON.stringify(items));
            //     // find pasted image among pasted items
            //     let blob = null;
            //     for (let i = 0; i < items.length; i++) {
            //         if (items[i].type.indexOf("image") === 0) {
            //             blob = items[i].getAsFile();
            //         }
            //     }
            //     // load image if there is a pasted image
            //     if (blob !== null) {
            //         let reader = new FileReader();
            //         reader.onload = function(event) {
            //             console.log("pasteClipboardHandler() loaded", event.target.result); // data url!
            //             let imageEl = annoElement.getSet().getImageElement();
            //             annoModel.imageSrc = event.target.result;
            //             imageEl.attr({ src: annoModel.imageSrc });
            //             console.log("new image source", imageEl.attr("src"));
            //
            //         };
            //         reader.readAsDataURL(blob);
            //     }
            //     event.preventDefault();
            // }

            function startEditingText( annoElement ) {
                //console.log("startEditingText");
                var rectElement = annoElement.getSet().getRectElement(),
                    textElement = annoElement.getSet().getTextElement(),
                    annoIndex = textElement.data( 'annoIndex' ),
                    annoModel = plotElement.objects[annoIndex],
                    input = textElement.inlineTextEditing.startEditing( {
                        bbox: annoModel.viewBBox,
                        color: annoModel.color,
                        bgColor: annoModel.bgColor }); // Retrieve created <input type=textarea> field}

                if( textElement.data( "inTextEdit" ) ) {
                    return;
                }
                textElement.data( "inTextEdit", true );
                rectElement.hide();
                plotElement.clearSelectedAdornments(); // temporarily hide non-text controls (close box etc)
                mediator.publish( "plot:startViewMode", {  // allow plot view background click to blur
                    mode: "text-editing",
                    textElement: textElement,
                    stopEditing: stopEditing
                } );

                input.addEventListener( "blur", function( e ) {
                    // Stop inline editing after blur on the text field
                    stopEditing( e );
                }, true );

                input.addEventListener( "keypress", function( e ) {
                    // Stop inline editing after entering Return/Enter key (key code 13)
                    var key = e.which || e.keyCode;
                    if( key === 13 ) {
                        stopEditing( e );
                    }
                }, false );

                function stopEditing( event ) {
                    if( textElement.data( "inTextEdit" ) ) {
                        textElement.data( "inTextEdit", false );
                        let saveData = plotElement.save(); // save pre-edit values for undo
                        if( event )
                            event.stopImmediatePropagation();
                        textElement.inlineTextEditing.stopEditing();
                        annoModel.text = textElement.attr( "text" ); // retrieve text for save/restore
                        annoModel.textWrapChange = "edit"; // flag to indicate text must be rewrapped
                        plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
                        plotElement.selectAdornment( textElement.data( "annoIndex" ) ); // reposition and show controls and restore selection
                        mediator.publish( "plot:stopViewMode", { mode: "text-editing" } ); // notify plot view
                        if( annoModel.text !== saveData.objects[annoIndex]?.text ) {
                            Dyn.mediator.publish( "plot:updateAdornment", {
                                saveData: saveData, // to create an undo step
                                restoreData: plotElement.save(), // save post-edit values for redo
                                actionString: Dyn.loc( "Edit Text" ),
                                updatePlot: false, // no need to update the plot view
                            } );
                        }
                    }
                }
            }

            function hoverIn( e ) {
                //console.log("hoverIn", this.type );
                let annoElement = this.getFocusElement(),
                    annoModel = plotElement._elemToModel( annoElement );
                if( annoModel.hasLock("forMove")) {
                    return;
                }
                annoElement.data( "hover", true );
                plotElement.prepareForDragEvents( true );
                if( this === annoElement && ! Dyn.plotView.isInDrawMode()) {
                    // fix flicker with image element above image rect, by not adding glow when hoverIn to image
                    // fix flicker with drawing new elements (especially Pencil), by not adding glow when drawing the shape
                    plotElement.addGlow( annoElement, Dyn.plotUtil.freehandGlowColor, Dyn.plotUtil.freehandGlowWidth );
                }
            }

            function hoverOut( e ) {
                //console.log("hoverOut", this.type );
                let annoElement = this.getFocusElement();
                if( !annoElement.data( "drag" ) ) {
                    // only if neither dragging nor hovering
                    plotElement.removeGlow( annoElement ); // remove glow
                    plotElement.prepareForDragEvents( false );
                }
                annoElement.data( "hover", false );
            }

            function dragStart( x, y, event ) {
                //console.log("dragStart");
                let annoElement = this.getFocusElement(),
                    annoIndex = annoElement.data( 'annoIndex' );
                annoElement.data( "movedBeforeClick", false ); // no movement yet
                if( plotElement._elemToModel( annoElement ).hasLock("forMove")) {
                    return;
                }
                annoElement.data( "drag", true );
                annoElement.attr( "cursor", Dyn.plotUtil.cursorMove );
                plotElement._saveObjPosition( annoIndex, xAxisView, yAxisView );
                Dyn.plotUtil.safeStopPropogation( event );
            }

            function dragMove( dx, dy, x, y, event ) {
                //console.log("dragMove");
                // Note: dx and dy are positions relative to starting position (not last dragMove)
                let annoElement = this.getFocusElement(),
                    annoIndex = annoElement.data( 'annoIndex' );
                if( plotElement._elemToModel( annoElement ).hasLock("forMove")) {
                    return;
                }
                annoElement.data( "movedBeforeClick", true ); // we have moved; ignore click
                // shift line: update both the element position and save the normalized coordinates
                plotElement._moveObjPosition( annotationAdornments, annoIndex, xAxisView, yAxisView, dx, dy );
                plotElement.selectAdornment( annoIndex ); // update position of closebox, etc
                plotElement.addGlow( this, Dyn.plotUtil.freehandGlowColor, Dyn.plotUtil.freehandGlowWidth ); // update the glow position
                if( Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1 ) {
                    annoElement.data( "wasMoved", true );
                }
                Dyn.plotUtil.safeStopPropogation( event );
            }

            function dragEnd( event ) {
                //console.log("dragEnd");
                let annoElement = this.getFocusElement();
                if( plotElement._elemToModel( annoElement ).hasLock("forMove")) {
                    return;
                }
                if( !annoElement.data( "hover" ) ) {
                    // only if neither dragging nor hovering
                    plotElement.removeGlow();
                    plotElement.prepareForDragEvents( false );
                }
                plotElement._endObjMoveResize( plotElement.selectedObjectIndex, annoElement.data( "wasMoved"));
                annoElement.data( "drag", false );
                annoElement.data( "wasMoved", false );
                Dyn.plotUtil.setAsKeyboardFocus( annoElement );
                plotElement.dragData = null;
                Dyn.plotUtil.safeStopPropogation( event );
            }

            /** click handler for path or image or text; called after dragStart/dragMove/dragEnd */
            function clickAnnotation( event ) {
                //console.log("clickAnnotation");
                let annoElement = this.getFocusElement(),
                    annoIndex = annoElement.data( 'annoIndex' ),
                    annoModel = plotElement.objects[annoIndex],
                    wasSelected = (plotElement.selectedObjectIndex === annoIndex),
                    clickSelectForTextUnlock = event && event.altKey && Dyn.plotUtil.isDrawModeAuthoring();
                annoElement.attr( "cursor", ! annoModel.hasLock("forMove") ? Dyn.plotUtil.cursorMove : Dyn.plotUtil.cursorDefault );
                plotElement.selectAdornment( annoIndex, undefined, clickSelectForTextUnlock );
                if( annoElement.data( "isText" )
                    && (! annoElement.data("movedBeforeClick")) // don't edit if at end of drag
                    && (! annoModel.hasLock("forTextEdit")) // don't edit if locked for text-editing
                    && (! clickSelectForTextUnlock) // don't edit if alt-click, so we can select and unlock
                    && (wasSelected || annoModel.hasLock("forMove"))) { // select instead of edit on 1st click, unless locked for move
                    startEditingText( annoElement );
                } else if(  Dyn.plotUtil.isDrawModeAuthoring() || !annoModel.hasLock(true)) {
                    Dyn.plotUtil.setAsKeyboardFocus( annoElement );
                }
            }

        }

        /**
         * {OVERRIDE} Deselect any selected adornments in this plot element.
         */
        clearSelectedAdornments() {
            let oldSelection = this.selectedLineIndex,
                annotateAdorns = this.getAdornmentsArray(),
                buttons = this._getButtons( annotateAdorns );

            // hide adornments that are shown during selection
            buttons.forEach( b => {
                if( Dyn.plotUtil.isElementVisible( b )) {
                    b.hide();
                }
            });
            AnnotationObject.hideControlPointsSet( this._getControlPointsSet( annotateAdorns ));
            this.selectedObjectIndex = -1;
            return( oldSelection !== this.selectedLineIndex );
        }

        /**
         * Select an adornment.  We only allow selection of annotation objects, not closebox etc.
         * @param annoIndex
         * @param [data] - if defined, the IDs of the objects to select
         * @param [overrideHideLocked] - if true, show the buttons even if locked for editing
         */
        selectAdornment( annoIndex, data, overrideHideLocked=false ) {
            let oldSelection = this.selectedLineIndex;
            if( annoIndex >= 0 && annoIndex < this.objects.length ) {
                this.selectedObjectIndex = annoIndex;
                this._repositionButtons( annoIndex, overrideHideLocked );
                this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
            } else {
                this.clearSelectedAdornments();
            }
            return( oldSelection !== this.selectedObjectIndex );
        }

        /**
         * Reposition/show/hide/update the annotation buttons/handles to match the given annotation element
         * (e.g. circle annotation, text annotation, etc ).
         * Also updates button parameters such as cursor type (which varies by annotation type).
         * Caller must ensure index is valid.  See also clearSelectedAdornments(), _getButtons()
         */
        _repositionButtons( annoIndex, overrideHideLocked ) {
            Dyn.assert(annoIndex >= 0 && annoIndex < this.objects.length, "invalid annotation element index");
            let annotateAdorns = this.getAdornmentsArray(),
                annoModel = this.objects[ annoIndex ],
                viewOnly = Dyn.preferences.isViewOnly(),
                showAll = (!viewOnly) && ( overrideHideLocked || ! annoModel.hasLock("forEdit"));
            // buttons to move/show/hide (see also _getButtons())
            let closeBox = this._getCloseBox( annotateAdorns ),
                contextMenu = this._getContextMenuButton( annotateAdorns ),
                controlPointsSet = this._getControlPointsSet( annotateAdorns ),
                resizeHandle1 = this._getResizeHandle( annotateAdorns, 1 ),
                resizeHandle2 = this._getResizeHandle( annotateAdorns, 2 );
            const pos = annoModel.viewBBox;

            annoModel.updateControlPointsSet( controlPointsSet );

            // position handles
            let showHandle1 = pos.numHandles >= 1 && showAll,
                showHandle2 = pos.numHandles >= 2 && showAll;
            if( showHandle1 ) {
                resizeHandle1.transform( "t" + pos.handle1[0] + "," + pos.handle1[1] );
                resizeHandle1.data( "cursor", pos.handle1[2] );
                resizeHandle1.toFront();
            }
            if( showHandle2 ) {
                resizeHandle2.transform( "t" + pos.handle2[0] + "," + pos.handle2[1] );
                resizeHandle2.data( "cursor", pos.handle2[2] );
                resizeHandle2.toFront();
            }
            Dyn.plotUtil.showOrHideElement( resizeHandle1, showHandle1 );
            Dyn.plotUtil.showOrHideElement( resizeHandle2, showHandle2 );

            // position context menu to right of (refX,refY), or to the left if too close to right edge of plot
            const paper = this.plotShaper.adornments.paper,
                paperBounds = new Dyn.mathUtil.Rect( 0, 0, paper.width, paper.height ),
                viewXRightEdge = Math.max( paperBounds.centerX(), paperBounds.right() - CONTEXT_MENU_WIDTH),
                wantMenuToLeft = (pos.refRight[0] > viewXRightEdge) && ! (pos.refLeft[0] < (paperBounds.right() - pos.refRight[0])),
                buttonSpacing = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ) + Dyn.plotUtil.plotButtonSpacing,
                buttonDirection = wantMenuToLeft ? -1 : 1, // -1=to right, -1
                xy = wantMenuToLeft ? pos.refLeft : pos.refRight;

            // position context menu to right of object reference point (or left if wantMenuToLeft)
            contextMenu.transform( "t" + (xy[0] + 1.0 * buttonSpacing * buttonDirection) + "," + xy[1] );
            contextMenu.data("wantMenuToLeft", wantMenuToLeft );
            Dyn.plotUtil.showOrHideElement( contextMenu, showAll );

            // position close box to right of context menu (or left if wantMenuToLeft)
            closeBox.transform( "t" + (xy[0] + 2.0 * buttonSpacing * buttonDirection) + "," + xy[1] );
            Dyn.plotUtil.showOrHideElement( closeBox, showAll );
        }

        /** show or hide controls for image capture or view only mode */
        setViewMode( mode ) {
            // NOTE: buttons may be null, that is OK
            let showControls = mode.showControls(),
                myAdornments = this.getAdornmentsArray(),
                buttons = this._getButtons( myAdornments );
            buttons.forEach( b =>
                Dyn.plotUtil.showOrHideElement( b, showControls ));
        }

        updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView ) {
            let annoModel = this.objects[ annoIndex ],
                annoElement = this._getAnnotationElement( annotationAdornments, annoIndex );
            Dyn.assert( annoElement );
            if( annoModel.isNonEmpty() ) {
                // use model to position the visible element created by calcAdornments()
                //console.log("updateElementFromNormalCoords", annoIndex, annoModel.getLocName());
                annoModel.updateElementFromModel( annoElement, xAxisView, yAxisView );
            } else {
                annoElement.attr( "path", "M0,0"); // clear path
                //annoElement.hide();
            }
        }

        /**
         * Add a point to the last freehand line. See also doAfterAddingPoints()
         * Called by the plotView drag handler while mouse moving in draw mode.
         * @param viewX
         * @param viewY
         * @param xAxisView
         * @param yAxisView
         * @param event - click or drag event for key modifiers
         */
        addPoint( ioAdornArray, viewX, viewY, xAxisView, yAxisView, event ) {
            let normalizedX = xAxisView.coordMap.destToFraction( viewX ),
                normalizedY = yAxisView.coordMap.destToFraction( viewY ),
                okToToAdd = true;
            const snap = AnnotateLayerElement.allowSnapToGrid( event ), dist = AnnotateLayerElement.getSnapDistance( event );

            if( this.objects.length === 0 ) {
                this.objects.push( new Dyn.AnnotationObject() );
            }
            let objectIndex = this.objects.length - 1,
                annoModel = this.objects[objectIndex], // only add points to last line, others are movable but not changable.
                isFirstPoint = (annoModel.normalizedPoints.length === 0);
            if( annoModel.isPencil() && !isFirstPoint ) {
                // Pencil: don't add point until we know it changes line (e.g. different from last point
                let lastViewX = annoModel.normalizedPoints[annoModel.normalizedPoints.length - 2].getViewCoord( xAxisView ),
                    lastViewY = annoModel.normalizedPoints[annoModel.normalizedPoints.length - 1].getViewCoord( yAxisView ),
                    distSquared = (viewX - lastViewX) * (viewX - lastViewX) + (viewY - lastViewY) * (viewY - lastViewY);
                okToToAdd = (distSquared >= (Dyn.plotUtil.pencilLineMinPixels*Dyn.plotUtil.pencilLineMinPixels));
                if( snap && annoModel.normalizedPoints.length > 4 ) { // if snapping and not first or second point
                    let x0 = annoModel.normalizedPoints[0].getViewCoord( xAxisView ),
                        y0 = annoModel.normalizedPoints[1].getViewCoord( yAxisView ),
                        toP0DistSquared = (viewX - x0) * (viewX - x0) + (viewY - y0) * (viewY - y0);
                    if( toP0DistSquared <= Math.pow(dist*2,2)) { // within snapping distance of 1st pencil point (double distance of usual snapping)
                        normalizedX = annoModel.normalizedPoints[0].n;
                        normalizedY = annoModel.normalizedPoints[1].n;
                    }
                }
            }

            if( okToToAdd ) {
                //console.log("AnnotateLayerElement add point distance squared="+distSquared);
                if( annoModel.isPencil() ) {
                    // push 2 coordinates to add a point to the freehand line, don't snap
                    annoModel.addNormalizedPoint( normalizedX );
                    annoModel.addNormalizedPoint( normalizedY );
                } else if( isFirstPoint ) {
                    // push 4 coordinates to start the bounding box of the rect, circle, arrow, text, image
                    annoModel.addNormalizedPoint( normalizedX ).updateFromViewCoord( viewX, xAxisView, snap, dist );
                    annoModel.addNormalizedPoint( normalizedY ).updateFromViewCoord( viewY, yAxisView, snap, dist );
                    annoModel.addNormalizedPoint( normalizedX + 1e-4 ) // + tiny offset so rectangle has non-zero area, less than one pixel
                    annoModel.addNormalizedPoint( normalizedY + 1e-4 );
                } else {
                    // if 3rd point of rect, circle, arrow, image: update 2nd point instead, since 2 points make a circle or rect or arrow
                    // NOTE: when adding points here, 0,1 are the starting point, and 2,3 are the ending point coordinates (not view normalized).
                    annoModel.normalizedPoints[annoModel.normalizedPoints.length - 2].updateFromViewCoord( viewX, xAxisView, snap, dist );
                    annoModel.normalizedPoints[annoModel.normalizedPoints.length - 1].updateFromViewCoord( viewY, yAxisView, snap, dist );
                }

                // update objects
                this.calcAdornments( ioAdornArray, null, xAxisView, yAxisView, false );
                //this.selectAdornment( objectIndex );
            }
        }

        /** handle mouse up after adding points to a new object. See also addPoint() */
        doAfterAddingPoints( ioAdornArray, xAxisView, yAxisView ) {
            if( this.objects.length > 0 ) {
                let objectIndex = this.objects.length - 1,
                    annoModel = this.objects[objectIndex],
                    annotationElement = this._getAnnotationElement( ioAdornArray, objectIndex );
                this.selectAdornment( objectIndex );
                Dyn.plotUtil.setAsKeyboardFocus( annotationElement.getFocusElement() );
                if( annoModel.isText() && this.kEditTextOnCreate ) {
                    startTextEdit( this, ioAdornArray );
                }
                if( annoModel.isPencil()) {
                    smoothPencilLines( this, ioAdornArray );
                }
            }
            function startTextEdit( plotElement, ioAdornArray ) {
                let objectIndex = plotElement.objects.length - 1;
                setTimeout( () => {
                    // start editing text, if current object still exists
                    let annotationElement = plotElement._getAnnotationElement( ioAdornArray, objectIndex ),
                        clickableRectElement = annotationElement?.getRectElement();
                    if( clickableRectElement ) Dyn.plotUtil.triggerRaphaelEvent( clickableRectElement, "click" );
                }, 100 );
            }
            function smoothPencilLines( plotElement, ioAdornArray ) {
                let objectIndex = plotElement.objects.length - 1;
                setTimeout( () => {
                    // smooth pencil line, if current object still exists after 100ms
                    let annoModel = plotElement.objects[objectIndex];
                    if( annoModel ) {
                        annoModel.setPencilSmoothing( Dyn.plotUtil.pencilSmoothingDefault );
                        // update objects and selection
                        plotElement.calcAdornments( ioAdornArray, null, xAxisView, yAxisView, false );
                        plotElement.selectAdornment( objectIndex );
                    }
                }, 100 );
            }
        }

        _getCloseBox( annotateAdorns ) {
            if( annotateAdorns.length >= this.kNumFixedAdornments ) {
                return annotateAdorns[annotateAdorns.length - 1];
            }
            return null;
        }

        _getContextMenuButton( annotateAdorns ) {
            if( annotateAdorns.length >= this.kNumFixedAdornments ) {
                return annotateAdorns[annotateAdorns.length - 2];
            }
            return null;
        }

        _getControlPointsSet( annotateAdorns ) {
            if( annotateAdorns.length >= this.kNumFixedAdornments ) {
                return annotateAdorns[annotateAdorns.length - 3];
            }
            return null;
        }

        _getResizeHandle( annotateAdorns, which ) {
            Dyn.assert( which>=1 && which<=2 );
            if( annotateAdorns.length >= this.kNumFixedAdornments ) {
                return annotateAdorns[annotateAdorns.length - ((which===1)?5:4)];
            }
            return null;
        }

        _getButtons( annotateAdorns ) {
            return [
                this._getCloseBox( annotateAdorns ),
                this._getContextMenuButton( annotateAdorns ),
                this._getControlPointsSet( annotateAdorns ),
                this._getResizeHandle( annotateAdorns, 1 ),
                this._getResizeHandle( annotateAdorns, 2 ) ];
        }

        // get the annotation element (Raphael element in the plot view paper) for the given annotation object.
        _getAnnotationElement( annotateAdorns, objectIndex ) {
            var partIndex = 0,// for now only one adornment part per line, so it has index 0
                adornmentIndex = ( objectIndex * this.kNumAdornmentsPerLine ) + partIndex;
            if( annotateAdorns.length >= (adornmentIndex + this.kNumFixedAdornments) ) {
                return annotateAdorns[ adornmentIndex ];
            }
            return null;
        }

        _getDeleteHandler( ) {
            function deleteSelectedAnnotationHandler( e ) {
                // handler to remove the selected annotation
                var annotateAdorns = plotElement.getAdornmentsArray(),
                    objectIndex = plotElement.selectedObjectIndex;

                if( objectIndex >= 0 && objectIndex < plotElement.objects.length ) {
                    let annoModel = plotElement.objects[objectIndex],
                        annoElement = plotElement._getAnnotationElement( annotateAdorns, objectIndex ),
                        willEmptyElement = plotElement.objects.every((model,i) => (model.closed || i===objectIndex));
                    plotElement.removeGlow( annoElement.getFocusElement() ); // remove glow
                    if( willEmptyElement ) {
                        // closing the only adornment, so we just remove the entire plot element with undo
                        mediator.publish( "plot:removePlotElement", { controlID: plotElement.primaryType } );
                    } else {
                        // erase line
                        let saveData = plotElement.save();
                        if( annoModel ) {
                            annoModel.normalizedPoints = [];
                            annoModel.closed = true;
                        }
                        plotElement.updateAnnotationTypes();
                        plotElement.clearSelectedAdornments();

                        // refresh the adornments
                        plotElement.calcAdornments( annotateAdorns, null, Dyn.plotView.xAxis, Dyn.plotView.yAxis );
                        Dyn.assert( plotElement.objects.some((model) => !(model.closed)), "annotate element should have annotations left after delete");

                        mediator.publish( "plot:removedOneAdornment", null ); // just update toolbar state
                        Dyn.mediator.publish( "plot:updateAdornment", {
                            saveData: saveData, // to create an undo step
                            restoreData: plotElement.save(), // save post-delete state for redo
                            actionString: Dyn.loc( "Remove {{action}}", { action:annoModel.getLocName() } ),
                            updatePlot: false, // no need to update the plot view
                        } );
                    }
                }
                e.stopPropagation(); // prevents event going to leaflet/map layer
                e.preventDefault();
            }

            var plotElement = this;
            return deleteSelectedAnnotationHandler;
        }

        /**
         * Make a close box for the annotations.
         * Caller must position the close box to the currently selected annotation, or hide.
         * @returns {*}
         */
        makeCloseBox( ) {

            function buttonHoverIn( event ) {
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgDownColor });
                this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: "none" } );
                if( this.data("tooltip"))
                    Dyn.toolTip.show( this, event, { x:this.getBBox().x2, y:this.getBBox().y2 });
            }

            function buttonHoverOut( event ) {
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgUpColor });
                this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
                if( Dyn.toolTip.isMyElement( this ))
                    Dyn.toolTip.hide();
            }

            var deleteOrClickHandler = this._getDeleteHandler(),
                closebox = Dyn.plotUtil.makeCloseBox( this.plotShaper.adornments.paper,
                    buttonHoverIn, buttonHoverOut, deleteOrClickHandler, { deleteHandler: deleteOrClickHandler } )
                    .data("tooltip", Dyn.loc('remove annotation')),
                cover = closebox.getCoverElement();

            return closebox;
        }

        /**
         * Make a context menu button for the annotations.
         * Caller must position the button to the currently selected annotation, or hide.
         * @returns {*} -- Raphael button set element
         */
        makeContextMenuButton( annotationAdornments, xAxisView, yAxisView ) {
            function buttonClick( e ) {
                // handler to pick line color, called by click or keydown event
                let annotateAdorns = plotElement.getAdornmentsArray(),
                    objectIndex = plotElement.selectedObjectIndex,
                    annoElement = plotElement._getAnnotationElement( annotateAdorns, objectIndex ),
                    annoModel = plotElement.objects[objectIndex],
                    contextMenu = plotElement._getContextMenuButton( annotateAdorns );
                if( annoElement && annoModel ) {
                    let xy = Dyn.plotUtil.clickOrKeyboardEventToPaperYX( e, annoElement ),
                        initialState = {
                            colorHex:annoModel.color,
                            colorsArray: Dyn.plotUtil.annotationColors,
                            isFilled: annoModel.allowFillOnOffControl()? annoModel.isFilled: undefined,
                            lineWidth: annoModel.width,
                            lineWidthMin: annoModel.getMinWidth(),
                            layerNum: annoModel.layerNum || 0,
                            layerOptions: AnnotationObject.layerOptions,
                            allowLock: Dyn.plotUtil.isDrawModeAuthoring(), // else hide lock controls
                            lock: annoModel.isLocked,
                            allowSmoothing: annoModel.isPencil(),
                            smoothing: annoModel.smoothing || 0,
                            isText: annoModel.isText(), // allow extra text controls? (see below)
                    };
                    if( initialState.isText ) {
                        initialState.colorsArray = Dyn.plotUtil.textAnnotationColors;
                        initialState = { ...initialState, ...{
                                bgColor: annoModel.bgColor,
                                bgColorsArray: Dyn.plotUtil.textAnnotationColors,
                                textSize: annoModel.fontSize
                            }};
                    }
                    setTimeout( () => { // delayed call is required to show when e.type==='keykdown', reason unknown
                        ContextMenuUtil.show( {x:xy.x, y:xy.y, alignRight: contextMenu.getCoverElement().data("wantMenuToLeft")}, initialState, ( data, type ) => {
                            switch( type ) {
                                case "bgColor":
                                    updateBackgroundColor( annoElement, annoModel, data?.hex || data?.color?.hex );
                                    break;
                                case "fgColor":
                                    updateForegroundColor( annoElement, annoModel, data?.hex || data?.color?.hex );
                                    break;
                                case "textSize":
                                    updateTextElementFontSize( annoElement, annoModel, data?.textSize );
                                    break;
                                case "lineWidth":
                                    updateLineWidth( annoElement, annoModel, data?.lineWidth );
                                    break;
                                case "layerNum":
                                    updateLayerNum( annoElement, annoModel, data?.layerNum );
                                    break;
                                case "lock":
                                    updateLock( annoElement, annoModel, data?.lock );
                                    break;
                                case "isFilled":
                                    updateFilled( annoElement, annoModel, data?.isFilled );
                                    break;
                                case "smoothing":
                                    updateSmoothing( annoElement, annoModel, data?.smoothing );
                                    break;
                            }
                        }, Dyn.plotUtil.annotateContextMenuID, Dyn);
                    }, 100 );
                }
            }
            function updateForegroundColor( annoElement, annoModel, colorStr ) {
                if( colorStr ) {
                    annoModel.color = colorStr; // update model for later save
                    let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                    plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
                }
            }
            function updateBackgroundColor( annoElement, annoModel, colorStr ) {
                if( colorStr ) {
                    annoModel.bgColor = colorStr; // update model for later save
                    annoModel.setFilled( true ); // turn on fill, if not on already, so user can see the color
                    let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                    plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
                }
            }
            function updateTextElementFontSize( annoElement, annoModel, fontSize ) {
                if( fontSize > 1 ) {
                    // update model for later save
                    //  Note: we could add undo here: oldFontSize = annoModel.fontSize, etc.
                    let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                    annoModel.fontSize = Dyn.mathUtil.clipToMinMax(
                        Dyn.plotUtil.textAnnotationSizeMin, Dyn.plotUtil.textAnnotationSizeMax, fontSize );
                    annoModel.textWrapChange = "fontsize"; // flag need for text re-wrapping after font size change
                    // update view
                    plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
                    plotElement.selectAdornment( annoIndex ); // update position of closebox, etc
                    plotElement.addGlow( annoElement, Dyn.plotUtil.freehandGlowColor, Dyn.plotUtil.freehandGlowWidth ); // update the glow position
                }
            }
            function updateLineWidth( annoElement, annoModel, lineWidth ) {
                if( lineWidth >= 0 ) {
                    // update model for later save
                    //  Note: we could add undo here: oldLineWidth = annoModel.width, etc.
                    let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                    annoModel.width = Dyn.mathUtil.clipToMinMax(
                        annoModel.getMinWidth(), Dyn.plotUtil.annotationLineWidthMax, lineWidth );
                    plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
                    plotElement.selectAdornment( annoIndex ); // update control points, if any, to new width
                }
            }
            function updateLayerNum( annoElement, annoModel, layerNum ) {
                if( Number.isFinite( layerNum )) {
                    annoModel.layerNum = layerNum;
                    plotElement.updateLayers();
                }
            }
            function updateLock( annoElement, annoModel, lock ) {
                let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                annoModel.setLock( lock );
                annoElement.attr( "cursor", ! annoModel.hasLock("forMove") ? Dyn.plotUtil.cursorMove : Dyn.plotUtil.cursorDefault );
                plotElement.selectAdornment( annoIndex ); // update controls
            }
            function updateFilled( annoElement, annoModel, wantFilled ) {
                annoModel.setFilled( wantFilled );
                let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
            }
            function updateSmoothing( annoElement, annoModel, smoothing ) {
                let annoIndex = annoElement.getFocusElement().data( 'annoIndex' );
                annoModel.setPencilSmoothing( smoothing ); // update model for later save
                plotElement.updateElementFromNormalCoords( annotationAdornments, annoIndex, xAxisView, yAxisView );
                plotElement.selectAdornment( annoIndex ); // update control points, if any
            }

            var plotElement = this,
                buttonSet = Dyn.plotUtil.makeContextMenuButton(
                    this.plotShaper.adornments.paper,
                    buttonClick,
                    { deleteHandler: this._getDeleteHandler() },
                    Dyn.loc( "annotation menu" ), Dyn.plotUtil.annotateContextMenuID );

            return buttonSet;
        }

        /*** make a resize handle for the annotations. We can have multiple resize handles, as needed.
         * Caller must position handle to the currently selected annotation, or hide if not in use.
         * @returns {*} -- Raphael button set
         */
        makeResizeHandle( annotationAdornments, xAxisView, yAxisView, buttonNum ) {

            function buttonHoverIn( e ) {
                var resizeButtonSymbol = this;
                e.currentTarget.style.cursor = resizeButtonSymbol.data( "cursor" );
                resizeButtonSymbol.attr( hoverInAttrs );
                plotElement.prepareForDragEvents( true ); // issue #1532 for map plot
            }

            function buttonHoverOut( e ) {
                var resizeButtonSymbol = this;
                e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                resizeButtonSymbol.attr( hoverOutAttrs );
                plotElement.prepareForDragEvents( false ); // issue #1532 for map plot
            }

            function dragStart( x, y, event ) {
                var resizeButtonCover = this.getCoverElement(),
                    resizeButtonSymbol = this.getSymbolElement(),
                    objectIndex = plotElement.selectedObjectIndex,
                    annoModel = plotElement.objects[objectIndex];
                if( annoModel.hasLock("forEdit") ) {
                    return;
                }
                resizeButtonCover.data( "drag", true );
                resizeButtonCover.data( "startFontSize", annoModel.fontSize );
                resizeButtonSymbol.attr( "cursor", Dyn.plotUtil.cursorMove );
                plotElement._saveObjPosition( objectIndex, xAxisView, yAxisView );
                Dyn.plotUtil.safeStopPropogation( event );
            }

            function dragMove( dx, dy, x, y, event ) {
                // Note: dx and dy are positions relative to starting position (not last dragMove)
                // if( plotElement.ignoreDragEvents() )
                //     return;
                var resizeButtonCover = this.getCoverElement(),
                    resizeButtonSymbol = this.getSymbolElement(),
                    objectIndex = plotElement.selectedObjectIndex,
                    annoElement = plotElement._getAnnotationElement( plotElement.getAdornmentsArray(), objectIndex ),
                    startFontSize = resizeButtonCover.data( "startFontSize" ),
                    buttonNum = resizeButtonCover.data( "which" ),
                    annoModel = plotElement.objects[objectIndex];
                if( annoModel.hasLock("forEdit") ) {
                    return;
                }
                // scale annotation object: update both the element position and the normalized coordinates
                plotElement._resizeObjPosition( annotationAdornments, objectIndex, xAxisView, yAxisView, dx, dy, startFontSize, buttonNum, event );
                plotElement.selectAdornment( objectIndex ); // update position of closebox, etc
                plotElement.addGlow( annoElement, Dyn.plotUtil.freehandGlowColor, Dyn.plotUtil.freehandGlowWidth ); // update the glow position
                resizeButtonSymbol.attr( "cursor", Dyn.plotUtil.cursorMove );
                if( Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1 ) {
                    resizeButtonCover.data( "wasMoved", true );
                }
                Dyn.plotUtil.safeStopPropogation( event );
            }

            function dragEnd( event ) {
                let annoModel = plotElement.objects[plotElement.selectedObjectIndex];
                if( annoModel.hasLock("forEdit") ) {
                    return;
                }
                let resizeButtonCover = this.getCoverElement();
                if( !resizeButtonCover.data( "hover" ) ) {
                    // only if neither dragging nor hovering
                    plotElement.removeGlow();
                }
                Dyn.plotUtil.setAsKeyboardFocus( resizeButtonCover );
                plotElement._endObjMoveResize( plotElement.selectedObjectIndex, resizeButtonCover.data( "wasMoved"));
                resizeButtonCover.data( "wasMoved", false );
                resizeButtonCover.data( "drag", false );
                plotElement.dragData = null;
                Dyn.plotUtil.safeStopPropogation( event );
            }

            function arrowKeyHandler( dx, dy, event ) {
                // let annoModel = plotElement.objects[plotElement.selectedObjectIndex],
                //     isText = annoModel ? annoModel.isText() : false;
                // // move one pixel in arrow's direction
                // if( isText ) {
                //     // for text object, override to increase font size 1 point for 1 arrow key press
                //     dx = dx / plotElement.textFontSizeChangePerPixel;
                //     dy = dy / plotElement.textFontSizeChangePerPixel;
                // }
                dragStart.call( resizeButtonSet, event );
                dragMove.call( resizeButtonSet, dx, dy, undefined, undefined, event );
                dragEnd.call( resizeButtonSet, event );
            }

            var hoverOutAttrs = {
                    stroke: Dyn.plotUtil.handleUpColor,
                    fill: "none" /*"white", 'fill-opacity':Dyn.plotUtil.opacityInvisibleButDraggable*/
                },
                hoverInAttrs = {
                    stroke: Dyn.plotUtil.handleUpColor,
                    fill: Dyn.plotUtil.axisButtonDownColor /*, 'fill-opacity':1 */
                },
                plotElement = this,
                paper = this.plotShaper.adornments.paper,
                width = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.resizeHandleSize ),
                radius = 0.5 * width,
                rectangleElement = paper.rect( -radius, -radius, width, width ),
                resizeButtonSet = Dyn.plotUtil.makeSquareButton( paper, rectangleElement,
                    buttonHoverIn, buttonHoverOut, null, {
                        arrowKeyHandler: arrowKeyHandler,
                        deleteHandler: this._getDeleteHandler()
                    }, Dyn.loc( "Resize" ) ),
                viewOnly = Dyn.preferences.isViewOnly();

            resizeButtonSet
                .data( "cursor", "ne-resize" )
                .data( "which", buttonNum );
            resizeButtonSet.getSymbolElement()
                .attr( hoverOutAttrs );
            if( !viewOnly ) {
                resizeButtonSet.getCoverElement()
                    .drag( dragMove.bind( resizeButtonSet ), dragStart.bind( resizeButtonSet ), dragEnd.bind( resizeButtonSet ) );
            }
            return resizeButtonSet;
        }

        /** make an empty set to hold the control points of the currenly selected object, if it has control points */
        makeControlPointsSet( annotationAdornments ) {
            let paper = this.plotShaper.adornments.paper;
            // make an empty set to start
            return paper.set();
        }

        /**
         * Save the annotation object's coordinates in the view, so we can move/transform them dynamically.
         * Used when the mouse is down only.
         * @param objectIndex
         * @private
         */
        _saveObjPosition( objectIndex, xAxisView, yAxisView ) {

            let annoModel = this.objects[objectIndex],
                viewPoints = [];

            annoModel.normalizedPoints.forEach( ( naCoord, index ) => {
                let axisView = Dyn.mathUtil.isEven( index ) ? xAxisView : yAxisView;
                viewPoints.push( naCoord.getViewCoord( axisView ) );
            } );
            this.dragData = {
                undoData: this.save(),
                viewPoints: viewPoints,
                viewBBox: new Dyn.mathUtil.Rect( annoModel.viewBBox ),
                view: Object.assign( {}, annoModel.viewBBox ) // other view reference points
            };
            Dyn.assert( annoModel.normalizedPoints.length === this.dragData.viewPoints.length );
        }

        /**
         * Move (drag) the selected annotation object in the plot.
         * @param annotationAdornments
         * @param objectIndex
         * @param xAxisView
         * @param yAxisView
         * @param dx
         * @param dy
         * @private
         */
        _moveObjPosition( annotationAdornments, objectIndex, xAxisView, yAxisView, dx, dy ) {
            let annoModel = this.objects[objectIndex],
                viewPoints = this.dragData.viewPoints;

            viewPoints.forEach( ( viewCoord, index ) => {
                var isXAxis = Dyn.mathUtil.isEven( index ),
                    axisView = isXAxis ? xAxisView : yAxisView,
                    newValue = viewCoord + (isXAxis ? dx : dy);
                annoModel.normalizedPoints[index].updateFromViewCoord( newValue, axisView );
            } );
            this.updateElementFromNormalCoords( annotationAdornments, objectIndex, xAxisView, yAxisView );
            Dyn.assert( annoModel.normalizedPoints.length === viewPoints.length );
        }

        static allowSnapToGrid( event ) {
            return ! event.shiftKey;
        }
        static getSnapDistance( event ) {
            return Dyn.plotUtil.annotationLineSnapDist * ((event.ctrlKey || event.metaKey)? 2:1);
        }

        /**
         * Resize the given annotation object (part of resize handle callback)
         * @param annotationAdornments
         * @param objectIndex
         * @param xAxisView
         * @param yAxisView
         * @param dx -- movement of resize handle in X since mouse down
         * @param dy -- movement of resize handle in Y since mouse down
         * @param event -- for key modifiers
         * @private
         */
        _resizeObjPosition( annotationAdornments, objectIndex, xAxisView, yAxisView, dx, dy, startFontSize, buttonNum, event ) {
            let annoModel = this.objects[objectIndex],
                viewBBox = this.dragData.viewBBox,
                viewPoints = this.dragData.viewPoints,
                coordIndex = this.dragData.view.coordIndex;
            const snap = AnnotateLayerElement.allowSnapToGrid( event ), dist = AnnotateLayerElement.getSnapDistance( event );

            // adjust the normalized points
            if( annoModel.isArrow() || annoModel.isStraightLine() ) {
                // "resize" handle repositions the first or last point
                let px = buttonNum===1 ? 0 : viewPoints.length - 2,
                    py = buttonNum===1 ? 1 : viewPoints.length - 1;
                annoModel.normalizedPoints[px].updateFromViewCoord( viewPoints[px] + dx, xAxisView, snap, dist );
                annoModel.normalizedPoints[py].updateFromViewCoord( viewPoints[py] + dy, yAxisView, snap, dist );
            } else if( annoModel.isRectangle() || annoModel.isImage() ) {
                // button 1 adjusts bottom-left, button 2 adjusts top-right
                Dyn.assert( coordIndex && ! annoModel.isSinglePoint());
                let px = buttonNum===1 ? coordIndex.left : coordIndex.right,
                    py = buttonNum===1 ? coordIndex.bottom : coordIndex.top;
                annoModel.normalizedPoints[px].updateFromViewCoord( viewPoints[px] + dx, xAxisView, snap, dist );
                annoModel.normalizedPoints[py].updateFromViewCoord( viewPoints[py] + dy, yAxisView, snap, dist );
            } else if( annoModel.isText()) {
                //if( textCornerHandles ) {
                    // button 1 adjusts bottom-right, button 2 adjusts top-right
                    Dyn.assert( coordIndex && ! annoModel.isSinglePoint());
                    let cx = buttonNum===1 ? coordIndex.right : coordIndex.right,
                        cy = buttonNum===1 ? coordIndex.top : coordIndex.bottom;
                    annoModel.normalizedPoints[cx].updateFromViewCoord( viewPoints[cx] + dx, xAxisView, snap, dist );
                    annoModel.normalizedPoints[cy].updateFromViewCoord( viewPoints[cy] + dy, yAxisView, snap, dist );
                // } else {
                //     let xi0 = 0, xi1 = viewPoints.length - 2,
                //         center = viewBBox.centerX(),
                //         tx1 = Math.abs( center - viewPoints[buttonNum === 1 ? xi0 : xi1] - dx );
                //     annoModel.normalizedPoints[xi0].updateFromViewCoord( center - tx1, xAxisView );
                //     annoModel.normalizedPoints[xi1].updateFromViewCoord( center + tx1, xAxisView );
                // }
                annoModel.textWrapChange = "resize"; // flag need for text re-wrapping after bounding box resize
            } else if( annoModel.isCircle()) {
                let scaleCenterWidth = Math.max( 0.01,
                    (viewBBox.width + dx*((buttonNum===1)?2:-2)) * Dyn.mathUtil.invertSafely( viewBBox.width ) );
                viewPoints.forEach( (value, index) => {
                    let isXAxis = Dyn.mathUtil.isEven( index ),
                        axisView = isXAxis ? xAxisView : yAxisView,
                        newValue = isXAxis ?
                            scaleXFromCenter( value, viewBBox, scaleCenterWidth ) :
                            scaleYFromCenter( value, viewBBox, scaleCenterWidth );
                    annoModel.normalizedPoints[index].updateFromViewCoord( newValue, axisView, isXAxis, dist );
                } );
            } else {
                // NOTE: pencil has no resize handles, so will never reach this section
                Dyn.assert( false, "need _resizeObjPosition routine for " + annoModel.type );
            }

            // update the visible element to match
            this.updateElementFromNormalCoords( annotationAdornments, objectIndex, xAxisView, yAxisView );
            Dyn.assert( annoModel.normalizedPoints.length === viewPoints.length );

            function scaleXFromCenter( value, bbox, scale ) {
                var origin = bbox.centerX();
                return origin + ((value - origin) * scale);
            }

            function scaleYFromCenter( value, bbox, scale ) {
                var origin = bbox.centerY();
                return origin + ((value - origin) * scale);
            }
        }

        _endObjMoveResize( objectIndex, wasDragged ) {
            if( wasDragged ) {
                // save an undo step if the object was dragged
                Dyn.assert( this.dragData );
                let restoreData = this.save();
                    //annoModel = this.objects[objectIndex];
                restoreData.draggedObjectIndex = objectIndex; // for canUpdateCallBack below
                Dyn.mediator.publish( "plot:updateAdornment", {
                    saveData: this.dragData.undoData, // to create an undo step
                    restoreData: restoreData,
                    actionString: Dyn.loc( "Move {{action}}", { action: this.getUndoAction( { annoIndex:objectIndex }) } ),
                    updatePlot: false,
                    canUpdateCallBack:// update the last undo step only if dragging the same line
                        ( lastUndoData, newUndoData ) => {
                            return lastUndoData.restoreData && newUndoData.restoreData
                                && (lastUndoData.restoreData.primaryType === newUndoData.restoreData.primaryType)
                                && (lastUndoData.actionString === newUndoData.actionString)
                                && (lastUndoData.restoreData.draggedObjectIndex === newUndoData.restoreData.draggedObjectIndex);
                        }
                } );
            }
            this.dragData = null;
        }

        _elemToModel( annoElement ) {
            Dyn.assert( annoElement );
            return this.objects[ annoElement.data( 'annoIndex' ) ];
        }

        getPasteHandler( clipboardTypeStr ) {
            // if we have a selected image, and clipboard image data, return the handler to update the image source.
            let annoIndex = this.selectedObjectIndex,
                annoModel = (annoIndex >= 0) && this.objects[annoIndex],
                plotElement = this;
            if( annoModel && annoModel.isImage() && (! annoModel.hasLock("forEdit"))
                && clipboardTypeStr.indexOf( "image" ) === 0 ) {
                return ( pastedImageSrc ) => {
                    let annotateAdorns = plotElement.getAdornmentsArray(),
                        annoElement = plotElement._getAnnotationElement( annotateAdorns, annoIndex );
                    if( annoElement ) {
                        annoModel.updateImageSrc( annoElement, pastedImageSrc );
                    }
                }
            }
            return null;
        }

        /** set the layering of the adornment elements, from the AnnotationObject.layerNum */
        updateLayers() {
            // get the min/max layers of the annotation models (see AnnotationObject.layerOptions)
            let annotationAdornments = this.getAdornmentsArray(),
                minLayer= 0,
                maxLayer = 0,
                changed = false;
            this.objects.forEach( annoModel => {
                maxLayer = Math.max( maxLayer, annoModel.layerNum || 0 );
                minLayer = Math.min( minLayer, annoModel.layerNum || 0 );
            })
            // set the desired layer number of the annotation elements
            // minLayer-maxLayer maps to 0-(maxLayer-minLayer), to interact with other adornments
            // default is that all annotations and buttons are on layer 0.
            const buttonLayer = maxLayer-minLayer;
            annotationAdornments.forEach((annoElement, index) => {
                let annoModel = this.objects[index],
                    layerNum = annoModel ? ((annoModel.layerNum || 0) - minLayer) : buttonLayer;
                changed = this.plotShaper.setAdornmentLayer( layerNum, annoElement ) || changed;
            });
            // now re-layer in the view
            if( changed ) {
                this.plotShaper.layerAdornments();
            }
        }

        resetForDrawMode( savedAnnotationLayer ) {
            let deleteAnnotationLayer = true;
            if( savedAnnotationLayer ) {
                // restore the original annotations, keep this element
                this.restore( savedAnnotationLayer );
                deleteAnnotationLayer = false;
            } else {
                // else keep all annotations if any are locked
                if( this.objects.find( (annoModel) => annoModel.hasLock("forEdit"))) {
                    deleteAnnotationLayer = false;
                }
            }
            return deleteAnnotationLayer;
        }

        static pasteOffsetX = 0.03;
        static pasteOffsetY = 0.03;

        /** move the copied object position a bit, so it is visible as part of a paste operation */
        static offsetSavedDataForPaste( savedData ) {
            // get the offset for a pasted object, in normalized coordinates [0-1]
            let meanX = Dyn.mathUtil.meanOfNumericArray( savedData.normalizedPoints.filter((n,index) => Dyn.mathUtil.isEven( index ))),
                meanY = Dyn.mathUtil.meanOfNumericArray( savedData.normalizedPoints.filter((n,index) => ! Dyn.mathUtil.isEven( index ))),
                offsetX = this.pasteOffsetX,
                offsetY = this.pasteOffsetY;
            if( meanX < 0 || meanX > 1 ) {
                this.pasteOffsetX = -this.pasteOffsetX; // switch directions in X
                offsetX = 1.5 * this.pasteOffsetY; // don't overlap with second-to-last object
            }
            if( meanY < 0 || meanY > 1 ) {
                this.pasteOffsetY = -this.pasteOffsetY; // switch directions in Y
                offsetY = 1.5 * this.pasteOffsetY; // don't overlap with second-to-last object
            }
            // now offset the normalized coordinates
            savedData.normalizedPoints = savedData.normalizedPoints.map(( n, index ) =>
                n + (Dyn.mathUtil.isEven( index ) ? offsetX : offsetY ));
            // remove attribute coords, to honor normalizedPoints offset
            delete savedData.attribCoords;
        }

    } // class AnnotateLayerElement

    Dyn.AnnotateLayerElement = AnnotateLayerElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsAnnotations, Dyn.AnnotateLayerElement );
    Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsOldFreehandLine, function( savedData, partialPlotState ) {
        return Dyn.AnnotateLayerElement; // convert an old saved freehand line element to an annotation element on restore
    });

    Dyn.AnnotateLayerElement.prototype.kUndoAction = 'Annotate'; // NOTE: not used for annotations, see references to actionString
    Dyn.AnnotateLayerElement.prototype.kNumFixedAdornments = 5; // close box, context menu button, 2 resize handles, control-points set
    Dyn.AnnotateLayerElement.prototype.kNumAdornmentsPerLine = 1; // 1 line path
    Dyn.AnnotateLayerElement.prototype.kEditTextOnCreate = true;
    //Dyn.AnnotateLayerElement.prototype.kEditSelectedTextOnly = true; // one click to select, 2nd click to edit
    //Dyn.AnnotateLayerElement.prototype.textFontSizeChangePerPixel = 0.1; // change Font Size of text 0.1 points per 1 pixel of resize handle drag

} // wrapDynAnnotateElement
